import React from 'react';
import { Box } from '@mui/material';
import { stitchColors, stitchFonts } from '../theme/tokens';

export interface RichTextViewerProps {
  content: any;
  className?: string;
  style?: React.CSSProperties;
}

export const RichTextViewer: React.FC<RichTextViewerProps> = ({
  content,
  className,
  style,
}) => {
  if (!content) {
    return null;
  }

  // If content is a plain string
  if (typeof content === 'string') {
    return (
      <Box
        className={className}
        style={style}
        sx={{
          fontFamily: stitchFonts.headline,
          lineHeight: 1.8,
          color: stitchColors.onBackground,
          whiteSpace: 'pre-wrap',
        }}
      >
        {content}
      </Box>
    );
  }

  // Render TipTap JSON tree
  const renderNode = (node: any, index: number): React.ReactNode => {
    if (!node) return null;

    if (node.type === 'text') {
      let element: React.ReactNode = node.text || '';
      if (Array.isArray(node.marks)) {
        node.marks.forEach((mark: any) => {
          if (mark.type === 'bold') {
            element = <strong>{element}</strong>;
          } else if (mark.type === 'italic') {
            element = <em>{element}</em>;
          } else if (mark.type === 'underline') {
            element = <u>{element}</u>;
          }
        });
      }
      return <React.Fragment key={index}>{element}</React.Fragment>;
    }

    const children = Array.isArray(node.content)
      ? node.content.map((child: any, i: number) => renderNode(child, i))
      : null;

    const align = node.attrs?.textAlign;
    const textAlignStyle: React.CSSProperties = align ? { textAlign: align } : {};

    switch (node.type) {
      case 'doc':
        return <div key={index}>{children}</div>;
      case 'paragraph':
        return (
          <p key={index} style={{ margin: '0.6em 0', ...textAlignStyle }}>
            {children || '\u00A0'}
          </p>
        );
      case 'heading': {
        const level = node.attrs?.level || 1;
        const HeadingTag = `h${Math.min(Math.max(level, 1), 6)}` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag key={index} style={{ margin: '1em 0 0.5em', ...textAlignStyle }}>
            {children}
          </HeadingTag>
        );
      }
      case 'bulletList':
        return (
          <ul key={index} style={{ paddingLeft: '1.5em', margin: '0.6em 0' }}>
            {children}
          </ul>
        );
      case 'orderedList':
        return (
          <ol key={index} style={{ paddingLeft: '1.5em', margin: '0.6em 0' }}>
            {children}
          </ol>
        );
      case 'listItem':
        return <li key={index}>{children}</li>;
      case 'table':
        return (
          <table
            key={index}
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              margin: '1em 0',
              border: `1px solid ${stitchColors.outlineVariant}`,
            }}
          >
            <tbody>{children}</tbody>
          </table>
        );
      case 'tableRow':
        return <tr key={index}>{children}</tr>;
      case 'tableHeader':
        return (
          <th
            key={index}
            style={{
              border: `1px solid ${stitchColors.outlineVariant}`,
              padding: '8px 12px',
              backgroundColor: stitchColors.surfaceContainerHigh,
              fontWeight: 600,
              textAlign: 'left',
            }}
          >
            {children}
          </th>
        );
      case 'tableCell':
        return (
          <td
            key={index}
            style={{
              border: `1px solid ${stitchColors.outlineVariant}`,
              padding: '8px 12px',
              textAlign: 'left',
            }}
          >
            {children}
          </td>
        );
      case 'hardBreak':
        return <br key={index} />;
      default:
        return <div key={index}>{children}</div>;
    }
  };

  return (
    <Box
      className={className}
      style={style}
      sx={{
        fontFamily: stitchFonts.headline,
        lineHeight: 1.8,
        color: stitchColors.onBackground,
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          my: 2,
        },
        '& th, & td': {
          border: `1px solid ${stitchColors.outlineVariant}`,
          p: 1,
        },
        '& th': {
          bgcolor: stitchColors.surfaceContainerHigh,
          fontWeight: 600,
        },
      }}
    >
      {renderNode(content, 0)}
    </Box>
  );
};

export default RichTextViewer;
